import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { isDefined } from '@trimble-gcs/common';
import {
  ModusButtonModule,
  ModusFormFieldModule,
  ModusIconModule,
  ModusInputModule,
  ModusTooltipModule,
} from '@trimble-gcs/modus';
import { AutoFocusDirective } from '@trimble-gcs/ngx-common';
import { catchError, EMPTY, finalize } from 'rxjs';
import { ClipBox } from '../connect-3d-ext/clip-box/clip-box.models';
import { FeatureLayerState } from '../map/feature-layer/feature-layer.state';
import { getFeatureCrsName } from '../map/map.util';
import { ClipAndSaveService } from '../scandata/clip-and-save/clip-and-save.service';
import { ScandataState } from '../scandata/scandata.state';
import { clipAndSaveTranslations } from './clip-and-save.translations';

// Strip characters invalid in Windows/cloud file names as the user types.
const INVALID_FILE_NAME_CHAR_SET = new Set('<>:"/\\|?*'.split(''));

function sanitizeFileName(value: string | null | undefined): string {
  return [...(value ?? '')]
    .filter((char) => char.charCodeAt(0) >= 32 && !INVALID_FILE_NAME_CHAR_SET.has(char))
    .join('');
}

function stripExtension(value: string): string {
  return value.replace(/\.[^./\\]+$/, '');
}

@UntilDestroy()
@Component({
  selector: 'sd-clip-and-save',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModusButtonModule,
    ModusFormFieldModule,
    ModusIconModule,
    ModusInputModule,
    ModusTooltipModule,
    AutoFocusDirective,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './clip-and-save.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClipAndSaveComponent implements OnInit {
  private readonly clipAndSaveService = inject(ClipAndSaveService);
  private readonly store = inject(Store);

  readonly pointcloudIds = input.required<string[]>();
  readonly clipBox = input<ClipBox>();
  readonly geoJsonFilter = input<string>();

  readonly exitClipAndSaveClick = output<void>();
  readonly saved = output<void>();

  readonly translations = clipAndSaveTranslations();

  readonly nameControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, nonEmptyValidator],
  });

  private readonly nameStatus = toSignal(this.nameControl.statusChanges, {
    initialValue: this.nameControl.status,
  });

  readonly isSubmitting = signal(false);
  readonly submitError = signal<string | undefined>(undefined);
  readonly submitSuccess = signal(false);

  readonly isMapClip = computed(() => isDefined(this.geoJsonFilter()));

  readonly descriptionText = computed(() =>
    this.isMapClip() ? this.translations.descriptionMap.text : this.translations.description.text,
  );

  readonly dismissLabel = computed(() =>
    this.submitSuccess() ? this.translations.close.text : this.translations.cancel.text,
  );

  private readonly featureCollection = this.store.selectSignal(FeatureLayerState.featureCollection);

  readonly showCrsMismatchWarning = computed(() => {
    if (this.submitSuccess()) return false;

    const featureCollection = this.featureCollection();
    if (!isDefined(featureCollection)) return false;

    const crsNames = new Set<string>();
    for (const id of this.pointcloudIds()) {
      const feature = featureCollection.features.find((ftr) => ftr.id === id);
      if (!isDefined(feature)) continue;
      const crsName = getFeatureCrsName(feature);
      if (isDefined(crsName)) crsNames.add(crsName);
    }

    return crsNames.size > 1;
  });

  readonly disableSubmit = computed(
    () => this.isSubmitting() || this.submitSuccess() || this.nameStatus() !== 'VALID',
  );

  constructor() {
    this.nameControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      const cleaned = sanitizeFileName(value);
      if (cleaned !== value) {
        this.nameControl.setValue(cleaned, { emitEvent: false });
      }
    });
  }

  ngOnInit() {
    const firstId = this.pointcloudIds()[0];
    if (!isDefined(firstId)) return;
    const scans = this.store.selectSnapshot(ScandataState.scandata);
    const first = scans.find((scan) => scan.id === firstId);
    if (!isDefined(first)) return;
    this.nameControl.setValue(sanitizeFileName(stripExtension(first.name)));
  }

  cancelClick() {
    if (this.submitSuccess()) {
      this.saved.emit();
      return;
    }

    this.exitClipAndSaveClick.emit();
  }

  submitClick() {
    if (this.disableSubmit()) return;

    const name = this.nameControl.value.trim();
    const clipBox = this.clipBox();
    const geoJsonFilter = this.geoJsonFilter();

    if (!isDefined(clipBox) && !isDefined(geoJsonFilter)) return;

    this.submitError.set(undefined);
    this.setSubmitting(true);

    this.clipAndSaveService
      .submit({
        name,
        pointcloudIds: this.pointcloudIds(),
        clipBox,
        geoJsonFilter,
      })
      .pipe(
        catchError(() => {
          this.submitError.set(this.translations.submitError.text);
          return EMPTY;
        }),
        finalize(() => this.setSubmitting(false)),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.submitSuccess.set(true);
        this.nameControl.disable();
      });
  }

  private setSubmitting(submitting: boolean) {
    if (submitting) {
      this.nameControl.disable();
    } else if (!this.submitSuccess()) {
      this.nameControl.enable();
    }

    this.isSubmitting.set(submitting);
  }
}

function nonEmptyValidator(control: AbstractControl<string>): ValidationErrors | null {
  return (control.value ?? '').trim().length > 0 ? null : { required: true };
}
